/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-10-10 14:48:23
 */
import '../index.scss'
import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button, Layout, Modal } from 'antd'
import type { ModalStaticFunctions } from 'antd/es/modal/confirm'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import sensors, { sensorsRole } from '../../../utils/sensors'
import {
  queryBatchVerifyTask,
  cancelBatchVerifyTask
} from '../../../actions/verify'

import verify_bg from '../../../images/verify-auth/verify_bg.jpg'
import processImg from '../../../images/verify-auth/process.png'
import close_icon from '../../../images/verify-auth/process_close_icon.png'
import { setVerifyResult } from '../../../slices/verifyAuthSlice'
import useVerifyAuthMethods from '../useVerifyAuthMethods'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

// 验证结果项类型
interface VerifyResultItem {
  status: number
  [key: string]: any
}

// 验证结果类型
interface VerifyResult {
  list: VerifyResultItem[]
  status: string
  taskID: string
}

export default () => {
  const navigate = useNavigate()
  const params = useParams<{ id: string; num: string }>()
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const isVip = useAppSelector((state) => state.group.isVip)
  const myRole = useAppSelector((state) => state.group.myRole)
  const taskID = params.id || ''
  const totalNum = parseInt(params.num || '0', 10)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const breakModal = useRef<ReturnType<ModalStaticFunctions['confirm']> | null>(null)
  const [percent, setPercent] = useState(0)
  const percentRef = useRef(0)
  const isMounted = useRef<boolean>(false)
  const startTime = new Date().getTime()
  const [loading, setLoading] = useState(false)
  const [dotCount, setDotCount] = useState(1)
  const dotCountRef = useRef(0)
  const {
    isExceptionCheck,
    isUnknownCheck,
    isFakeCheck
  } = useVerifyAuthMethods()

  useEffect(() => {
    isMounted.current = true
    queryTask()
    return () => {
      isMounted.current = false
    }
  }, [taskID])

  useEffect(() => {}, [percent, dotCount])
  useEffect(() => {
    reportSaClickEvent('start')
  }, [])

  const queryTask = async () => {
    try {
      let res = await dispatch(queryBatchVerifyTask({ taskID }))
      const { status, taskStatus } = res
      if (status === 1) {
        if (isMounted.current) {
          if (taskStatus === 1) {
            setLoading(true)
            dotCountRef.current === 6
              ? (dotCountRef.current = 1)
              : dotCountRef.current++
            setDotCount(dotCountRef.current)
          } else {
            setLoading(false)
            percentRef.current += (Math.random() * 90) / totalNum
            setPercent(Math.min(parseInt(String(percentRef.current)), 90))
          }
        }
        timer.current = setTimeout(() => {
          queryTask()
        }, 1000) as ReturnType<typeof setTimeout>
        return
      } else if (isExceptionCheck(status)) {
        setLoading(false)
        reportSaClickEvent('finish', {
          processTime: new Date().getTime() - startTime
        })
        setPercent(100)
        reportSaClickEvent('showPopNetError', { errorCode: status })
        Modal.info({
          title: '网络连接出错',
          content: '网络信号不佳，请保持网络通畅，重新开始验真',
          okText: '我知道了',
          onOk: () => {
            reportSaClickEvent('clickPopNetErrorKnow', { errorCode: status })
            navigate('/verifyAuth/index?pageFrom=back')
          }
        })
      } else {
        setLoading(false)
        reportSaClickEvent('finish', {
          processTime: new Date().getTime() - startTime
        })
        setPercent(100)
        const list: VerifyResultItem[] = res.list || []
        const verifyResult: VerifyResult[] = []
        const successList: VerifyResultItem[] = []
        const failList: VerifyResultItem[] = []
        const unKnowList: VerifyResultItem[] = []
        list.forEach((ele) => {
          if (ele.status === 0) {
            successList.push(ele)
          } else if (isUnknownCheck(ele.status)) {
            unKnowList.push(ele)
          } else if (isFakeCheck(ele.status)) {
            failList.push(ele)
          }
        })
        if (
          failList.length === 0 &&
          unKnowList.length === 0 &&
          successList.length === 0
        ) {
          Modal.info({
            title: '照片验真失败',
            content: '请联系客服进行反馈',
            okText: '我知道了',
            onOk: () => {
              navigate('/verifyAuth/index?pageFrom=back')
            }
          })
          setLoading(false)
          return
        }
        verifyResult.push({
          list: failList,
          status: 'fail',
          taskID: taskID
        })
        verifyResult.push({
          list: unKnowList,
          status: 'unKnow',
          taskID: taskID
        })
        verifyResult.push({
          list: successList,
          status: 'success',
          taskID: taskID
        })
        dispatch(setVerifyResult(verifyResult as any))
        if (breakModal.current) {
          breakModal.current.destroy()
        }
        navigate('/verifyAuth/result')
      }
    } catch (error) {
      setLoading(false)
      reportSaClickEvent('showPopNetError')
      Modal.info({
        title: '网络连接出错',
        content: '网络信号不佳，请保持网络通畅，重新开始验真',
        okText: '我知道了',
        onOk: () => {
          navigate('/verifyAuth/index?pageFrom=back')
        }
      })
    }
  }
  const cancelTask = async () => {
    setLoading(false)
    reportSaClickEvent('break', {
      processTime: new Date().getTime() - startTime
    })
    try {
      if (timer.current) clearTimeout(timer.current)
      await dispatch(cancelBatchVerifyTask({ taskID }))
    } catch (error) {
      console.log('error', error)
    }
  }

  const reportSaClickEvent = (actionType: string, params: Record<string, any> = {}) => {
    let info = {}
    if (groupID) {
      info = {
        isGroupVIP: isVip,
        groupID: groupID,
        role: sensorsRole(myRole)
      }
    }
    sensors.track('web_click_page_verification_process', {
      actionType: actionType,
      totalNum: totalNum,
      ...params,
      ...info
    })
  }

  const handleVerifyRecordClick = () => {
    Modal.confirm({
      title: '验真记录 正在开发中',
      content: '如有需要可点击下方催促开发',
      okText: '催促开发',
      onOk: () => console.log('这里需要增加一个埋点!')
    })
  }

  const handleBack = () => {
    reportSaClickEvent('clickProgressPopClose')
    breakModal.current = Modal.confirm({
      title: '是否中断验真？',
      content: '已上传的照片将保留，可点击重试',
      okText: '中断验真',
      cancelText: '继续验真',
      onOk: async () => {
        reportSaClickEvent('clickBreakVerificationPopBreak')
        await cancelTask()
        navigate('/verifyAuth/index?pageFrom=back')
      },
      onCancel: () => {
        reportSaClickEvent('clickBreakVerificationPopContinue')
      }
    })
  }

  return (
    <Layout className="verify-auth-process">
      <PageHeader
        className="Header"
        ghost={true}
        title={
          <>
            <span
              className="verify-auth-header"
              onClick={() => {
                cancelTask()
              }}
            >
              <span>照片验真</span>
            </span>

            <span
              className="title"
              style={{
                display: 'inline-block',
                margin: '0 8px',
                color: '#000'
              }}
            >
              &gt;
            </span>
            <span>
              <span>验真结果</span>
            </span>
          </>
        }
        extra={
          <>
            <Button
              onClick={() => {
                handleVerifyRecordClick()
              }}
            >
              验真记录
            </Button>
          </>
        }
      />
      <Content
        className="content"
        style={{
          backgroundImage: `url(${verify_bg})`,
          backgroundSize: 'cover'
        }}
      >
        <div className="content-container">
          <div className="image-container">
            <div className="close-icon" onClick={handleBack}>
              <img src={close_icon} alt="" />
            </div>
            <img src={processImg} alt="" className="process-image" />
            <div className="content-text">
              {loading ? (
                <div className="process-text">
                  验真服务准备中请稍等
                  <span
                    style={{
                      display: 'inline-block',
                      width: '40px',
                      textAlign: 'left'
                    }}
                  >
                    {Array(dotCount)
                      .fill(null)
                      .map((_, index) => (
                        <span key={index}>.</span>
                      ))}
                  </span>
                </div>
              ) : (
                <div className="process-text">照片正在验真中：{percent}%</div>
              )}
              <div className="process-info-text">
                为获得准确的验真结果，请保持网络通畅
              </div>
            </div>
          </div>
        </div>
      </Content>
    </Layout>
  )
}
